import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FormikProps, getIn, withFormik } from "formik";
import { Button, IconButton } from "@chakra-ui/button";
import { Box, Divider, Flex, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import * as Yup from "yup";
import { ApiResult } from "../api/dataApiClient";
import { CopyText } from "../components/Utilities/CopyText";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { PlusIcon } from "../styles/icons";
import { MemberLookupMethod } from "../enums/MemberLookupMethod";
import { v4 as uuid } from "uuid";
import { DeleteIcon } from "@chakra-ui/icons";
import { MemberFilterQueryModal } from "../components/MemberFilterQuery/MemberFilterQueryModal";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { EMLProgramType } from "../enums/EMLProgramType";
import { EMLDeliveryType } from "../enums/EMLDeliveryType";
import { cloneDeep, maxBy } from "lodash";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

const validationSchema = Yup.object().shape({
  DefaultDisbursementAccountId: Yup.string().matches(/^[A-Z0-9]{9}$/, {
    message: "only allow alphanumeric, uppercased, has to be 9 characters",
    excludeEmptyString: false,
  }),
  DefaultReloadableCardProgramInvitationSMSMessageBodyFormat: Yup.string()
    .notRequired()
    .test(
      "reloadableIncludeAppDownloadUrl",
      'You must include token "{AppDownloadUrl}"',
      (value) => value?.indexOf("{AppDownloadUrl}") != -1
    ),
  DefaultInstantGiftInvitationSMSMessageBodyFormat: Yup.string()
    .notRequired()
    .test(
      "instantIncludeAppDownloadUrl",
      'You must include token "{AppDownloadUrl}"',
      (value) => value?.indexOf("{AppDownloadUrl}") != -1
    )
    .test(
      "instantIncludeCardCode",
      'You must include token "{Card.Code}"',
      (value) => value?.indexOf("{Card.Code}") != -1
    ),
  SelfServeMemberTerminals: Yup.array().of(
    Yup.object().shape({
      ThemeData: Yup.string()
        .notRequired()
        .test("memberTerminalJsonInvalid", "Invalid json format", (value) => {
          if (emptyStrToNull(value as string) == null) return true;
          try {
            JSON.parse(value as string);
          } catch {
            return false;
          }
          return true;
        }),
      AllowedIPAddresses: Yup.array()
        .notRequired()
        .of(
          Yup.string()
            .required("Ip Address is required")
            .matches(
              /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/,
              {
                message: "invalid ip address",
                excludeEmptyString: false,
              }
            )
        ),
    })
  ),
  Programs: Yup.array().of(
    Yup.object().shape({
      key: Yup.number()
        .min(0, "Program Key must be greater than or equal to 0")
        .required("Program Key is required"),
      value: Yup.object().shape({
        Description: Yup.string().required("Description is required"),
        ProgramId: Yup.number()
          .min(0, "Program Id must be greater than or equal to 0")
          .required("Program Id is required"),
        CompanyId: Yup.string().required("Company Id is required"),
        DisbursementAccountId: Yup.string()
          .notRequired()
          .matches(/^[A-Z0-9]{9}$/, {
            message:
              "only allow alphanumeric, uppercased, has to be 9 characters",
            excludeEmptyString: false,
          }),
        MinimumInitialCardValue: Yup.number()
          .min(
            0,
            "Minimum Initial Card Value must be greater than or equal to 0"
          )
          .notRequired(),
        MaximumInitialCardValue: Yup.number()
          .min(
            0,
            "Maximum Initial Card Value must be greater than or equal to 0"
          )
          .required("Maximum Initial Card Value is required"),
        MinimumPointsTransferToCardValue: Yup.number()
          .min(
            0,
            "Minimum Points Transfer To Card Value must be greater than or equal to 0"
          )
          .notRequired(),
        MaximumPointsTransferToCardValue: Yup.number()
          .min(
            0,
            "Maximum Points Transfer To Card Value must be greater than or equal to 0"
          )
          .required("Maximum Points Transfer To Card Value is required"),
        MinimumAddFundsToCardValue: Yup.number()
          .min(
            0,
            "Minimum Add Funds To Card Value must be greater than or equal to 0"
          )
          .notRequired(),
        MaximumAddFundsToCardValue: Yup.number()
          .min(
            0,
            "Maximum Add Funds To Card Value must be greater than or equal to 0"
          )
          .required("Maximum Add Funds To Card Value is required"),
        CardOrderOTPValidationAttemptLimit: Yup.number()
          .notRequired()
          .min(
            1,
            "Card Order OTP Validation Attempt Limit must be greater than or equal to 1"
          ),
        FeeConfiguration: Yup.object()
          .notRequired()
          .shape({
            FundsLoadFeePercent: Yup.number()
              .notRequired()
              .min(
                0,
                "Funds Load Fee Percent must be greater than or equal to 0"
              ),
            LoyaltyPointsTransferToCardFeePercent: Yup.number()
              .notRequired()
              .min(
                0,
                "Loyalty Points Transfer To Card Fee Percent must be greater than or equal to 0"
              ),
          }),
        CardActivationConfiguration: Yup.object()
          .notRequired()
          .shape({
            CardActivationThemeData: Yup.string()
              .notRequired()
              .test(
                "cardActivationJsonInvalid",
                "Invalid json format",
                (value) => {
                  if (emptyStrToNull(value as string) == null) return true;
                  try {
                    JSON.parse(value as string);
                  } catch {
                    return false;
                  }
                  return true;
                }
              ),
          }),
        CardProgramTransferLimit: Yup.object()
          .notRequired()
          .shape({
            DollarLimit: Yup.number()
              .min(0, "Dollar Limit must be greater than or equal to 0")
              .required("Dollar Limit is required"),
            TimePeriod: Yup.object()
              .notRequired()
              .shape({
                Quantity: Yup.number()
                  .min(
                    1,
                    "Time Period Value must be greater than or equal to 1"
                  )
                  .required("Time Period Value is required"),
                Period: Yup.string().required("Time Period Type is required"),
              }),
          }),
      }),
    })
  ),
});

const memberTerminalView = (
  memberTerminals: CherryHubAdminApi.EmlMemberTerminalConfiguration[],
  memberTerminal: CherryHubAdminApi.EmlMemberTerminalConfiguration,
  memberTerminalIdx: number,
  memberLookupMethods: CherryHubAdminApi.EnumConfigurationItem[] | null,
  memberLookupMethodsLoading: boolean,
  allowableInputStringCharacters:
    | CherryHubAdminApi.EnumConfigurationItem[]
    | null,
  allowableInputStringCharactersLoading: boolean,
  fieldName: string,
  errors: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  onOpenMemberFilterQueryModal: () => void,
  setMemberFilterFieldName: React.Dispatch<React.SetStateAction<string>>,
  setExistingMemberFilter: React.Dispatch<
    React.SetStateAction<string | undefined>
  >
) => (
  <AccordionItem
    w="100%"
    key={memberTerminalIdx}
    className={`terminal-field-${memberTerminalIdx}`}
  >
    {({ isExpanded }) => (
      <>
        <h2>
          <AccordionButton textAlign="left" gap={4}>
            <Box flexShrink={1}>
              <b>{`Self Serve Member Terminal #${memberTerminal.TerminalId}`}</b>
            </Box>
            <Box flexGrow={1}>
              <IconButton
                aria-label="Delete self serve member terminal."
                title="Delete self serve member terminal."
                icon={<DeleteIcon />}
                size="xs"
                bgColor="cherryGrey.160"
                onClick={() => {
                  memberTerminals?.splice(memberTerminalIdx, 1);
                  setFieldValue("SelfServeMemberTerminals", memberTerminals);
                }}
              />
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel width="100%">
          {isExpanded && (
            <VStack w="100%" alignItems="flex-start">
              <SwitchField
                horizontal
                label="Is Enabled"
                name={`${fieldName}.IsEnabled`}
              />

              <ChakraReactSelectField
                horizontal
                label="Member Lookup Method"
                name={`${fieldName}.MemberLookupMethod`}
                selectedOptionColor="blue"
                isLoading={memberLookupMethodsLoading}
                options={
                  memberLookupMethods?.map((provider) => {
                    return {
                      label: provider.Value!,
                      value: provider.Key!,
                    };
                  }) || []
                }
              />

              <ChakraReactSelectField
                horizontal
                label="Allowable Input String Characters"
                name={`${fieldName}.AllowableInputStringCharacters`}
                selectedOptionColor="blue"
                allowEmpty
                isLoading={allowableInputStringCharactersLoading}
                options={
                  allowableInputStringCharacters?.map((provider) => {
                    return {
                      label: provider.Value!,
                      value: provider.Key!,
                    };
                  }) || []
                }
              />

              <TextField
                horizontal
                label="Member Sign In Prompt"
                name={`${fieldName}.MemberSignInPrompt`}
              />

              <TextField
                horizontal
                label="Logo Url"
                name={`${fieldName}.LogoUrl`}
              />

              <TextArea
                horizontal
                label="Member Filter Querystring"
                name={`${fieldName}.MemberFilterQuerystring`}
                isReadOnly
                rightElement={
                  <Button
                    onClick={() => {
                      setMemberFilterFieldName(
                        `${fieldName}.MemberFilterQuerystring`
                      );
                      setExistingMemberFilter(
                        memberTerminal.MemberFilterQuerystring ?? ""
                      );
                      onOpenMemberFilterQueryModal();
                    }}
                    alignSelf="flex-end"
                    colorScheme="cherryButton"
                  >
                    Filters
                  </Button>
                }
              />

              <Box
                w="100%"
                p={4}
                border="1px solid"
                borderColor="cherryButtonDark.100"
                borderRadius="md"
              >
                <HStack w="100%">
                  <Text mr="auto" fontSize="sm">
                    <b>Allowed IP Addresses</b>
                  </Text>

                  <Button
                    ml="auto"
                    mr="0"
                    size="xs"
                    leftIcon={<PlusIcon />}
                    onClick={() => {
                      if (memberTerminal.AllowedIPAddresses == null) {
                        setFieldValue(`${fieldName}.AllowedIPAddresses`, [""]);
                      } else {
                        memberTerminal.AllowedIPAddresses?.push("");
                        setFieldValue(
                          `${fieldName}.AllowedIPAddresses`,
                          memberTerminal.AllowedIPAddresses
                        );
                      }
                    }}
                    colorScheme="cherryButton"
                  >
                    New IP Address
                  </Button>
                </HStack>
                <Divider mb={2} mt={1} />
                <VStack w="100%">
                  {(memberTerminal.AllowedIPAddresses?.length &&
                    memberTerminal.AllowedIPAddresses?.map(
                      (ipAddress, ipAddressIdx) => (
                        <HStack
                          w="100%"
                          alignItems="flex-start"
                          key={ipAddressIdx}
                          spacing={2}
                        >
                          <TextField
                            horizontal
                            label={`IP Address ${ipAddressIdx + 1}`}
                            name={`${fieldName}.AllowedIPAddresses[${ipAddressIdx}]`}
                          />

                          {(memberTerminal.AllowedIPAddresses?.length ?? 0) >
                            0 && (
                            <Box h="100%" pb={1} alignSelf="flex-end">
                              <IconButton
                                aria-label="Delete recipient email address."
                                title="Delete recipient email address."
                                icon={<DeleteIcon />}
                                size="sm"
                                onClick={() => {
                                  memberTerminal.AllowedIPAddresses?.splice(
                                    ipAddressIdx,
                                    1
                                  );
                                  setFieldValue(
                                    `${fieldName}.AllowedIPAddresses`,
                                    memberTerminal.AllowedIPAddresses
                                  );
                                }}
                              />
                            </Box>
                          )}
                        </HStack>
                      )
                    )) || (
                    <Text fontStyle="italic" fontSize="sm">
                      Allowed IP Addresses has not been set.
                    </Text>
                  )}
                </VStack>
              </Box>

              <JsonTextAreaField
                horizontal
                label="Theme Data"
                name={`${fieldName}.ThemeData`}
                valueText={memberTerminal.ThemeData}
                errors={errors}
                setFieldValue={setFieldValue}
                rows={10}
              />
            </VStack>
          )}
        </AccordionPanel>
      </>
    )}
  </AccordionItem>
);

const programView = (
  programs: { key: string; value: CherryHubAdminApi.EmlCardProgram }[] | null,
  program: { key: string; value: CherryHubAdminApi.EmlCardProgram },
  programIdx: number,
  fieldName: string,
  programTypes: CherryHubAdminApi.EnumConfigurationItem[] | null,
  programTypesLoading: boolean,
  deliveryTypes: CherryHubAdminApi.EnumConfigurationItem[] | null,
  deliveryTypesLoading: boolean,
  emlParties: CherryHubAdminApi.EnumConfigurationItem[] | null,
  emlPartiesLoading: boolean,
  errors: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (
  <AccordionItem
    w="100%"
    key={programIdx}
    className={`program-field-${programIdx}`}
  >
    {({ isExpanded }) => (
      <>
        <h2>
          <AccordionButton textAlign="left" gap={4}>
            <Box flexShrink={1}>
              <b>{program.value.Description ?? `Program ${programIdx + 1}`}</b>
              &nbsp; - {`#${program.key}`}
            </Box>
            <Box flexGrow={1}>
              <IconButton
                aria-label="Delete custom form field."
                title="Delete custom form field."
                icon={<DeleteIcon />}
                size="xs"
                bgColor="cherryGrey.160"
                onClick={() => {
                  programs?.splice(programIdx, 1);
                  setFieldValue("Programs", programs);
                }}
              />
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel width="100%">
          {isExpanded && (
            <VStack w="100%" alignItems="flex-start">
              <SwitchField
                horizontal
                label="Is Enabled"
                name={`${fieldName}.value.IsEnabled`}
              />

              <TextField
                horizontal
                label="Program Key"
                name={`${fieldName}.key`}
              />

              <TextField
                horizontal
                label="Description"
                name={`${fieldName}.value.Description`}
              />

              <IntegerField
                horizontal
                label="Program Id"
                name={`${fieldName}.value.ProgramId`}
              />

              <TextField
                horizontal
                label="Company Id"
                name={`${fieldName}.value.CompanyId`}
              />

              <ChakraReactSelectField
                horizontal
                label="Program Type"
                name={`${fieldName}.value.ProgramType`}
                selectedOptionColor="blue"
                allowEmpty
                isLoading={programTypesLoading}
                options={
                  programTypes?.map((provider) => {
                    return {
                      label: provider.Value!,
                      value: provider.Key!,
                    };
                  }) || []
                }
              />

              <ChakraReactSelectField
                horizontal
                isMulti
                label="Delivery Types"
                name={`${fieldName}.value.DeliveryTypes`}
                selectedOptionColor="blue"
                allowEmpty
                isLoading={deliveryTypesLoading}
                options={
                  deliveryTypes?.map((provider) => {
                    return {
                      label: provider.Value!,
                      value: provider.Key!,
                    };
                  }) || []
                }
              />

              <TextField
                horizontal
                label="MDES Config Id"
                name={`${fieldName}.value.MDESConfigId`}
              />

              <TextField
                horizontal
                label="Disbursement Account Id"
                name={`${fieldName}.value.DisbursementAccountId`}
              />

              <IntegerField
                horizontal
                label="Minimum Initial Card Value"
                name={`${fieldName}.value.MinimumInitialCardValue`}
                precision={1}
              />

              <IntegerField
                horizontal
                label="Maximum Initial Card Value"
                name={`${fieldName}.value.MaximumInitialCardValue`}
                precision={1}
              />

              <IntegerField
                horizontal
                label="Minimum Add Funds To Card Value"
                name={`${fieldName}.value.MinimumAddFundsToCardValue`}
                precision={1}
              />

              <IntegerField
                horizontal
                label="Maximum Add Funds To Card Value"
                name={`${fieldName}.value.MaximumAddFundsToCardValue`}
                precision={1}
              />

              <IntegerField
                horizontal
                label="Minimum Points Transfer To Card Value"
                name={`${fieldName}.value.MinimumPointsTransferToCardValue`}
                precision={1}
              />

              <IntegerField
                horizontal
                label="Maximum Points Transfer To Card Value"
                name={`${fieldName}.value.MaximumPointsTransferToCardValue`}
                precision={1}
              />

              <SwitchField
                horizontal
                label="Lock Reloadable Card To Company"
                name={`${fieldName}.value.LockReloadableCardToCompany`}
              />

              <SwitchField
                horizontal
                label="Hide All Cards In Cherry Pay App"
                name={`${fieldName}.value.HideAllCardsInCherryPayApp`}
              />

              <IntegerField
                horizontal
                label="Default Offer Expiry Days"
                name={`${fieldName}.value.DefaultOfferExpiryDays`}
              />

              <IntegerField
                horizontal
                label="Default Plastic Expiry Months"
                name={`${fieldName}.value.DefaultPlasticExpiryMonths`}
              />

              <IntegerField
                horizontal
                label="Card Order OTP Validation Attempt Limit"
                name={`${fieldName}.value.CardOrderOTPValidationAttemptLimit`}
              />

              <Box
                w="100%"
                p={4}
                border="1px solid"
                borderColor="cherryButtonDark.100"
                borderRadius="md"
              >
                <HStack w="100%">
                  <Text mr="auto" fontSize="sm">
                    <b>Fee Configuration</b>
                  </Text>
                </HStack>

                <Divider mb={2} mt={1} />

                <VStack w="100%">
                  <>
                    <ChakraReactSelectField
                      horizontal
                      label="Funds Load Fee Payable By Party"
                      name={`${fieldName}.value.FeeConfiguration.FundsLoadFeePayableByParty`}
                      selectedOptionColor="blue"
                      isLoading={emlPartiesLoading}
                      options={
                        emlParties?.map((provider) => {
                          return {
                            label: provider.Value!,
                            value: provider.Key!,
                          };
                        }) || []
                      }
                    />

                    <IntegerField
                      horizontal
                      label="Funds Load Fee Percent"
                      name={`${fieldName}.value.FeeConfiguration.FundsLoadFeePercent`}
                      precision={1}
                    />

                    <ChakraReactSelectField
                      horizontal
                      label="Loyalty Points Transfer To Card Fee Payable By Party"
                      name={`${fieldName}.value.FeeConfiguration.LoyaltyPointsTransferToCardFeePayableByParty`}
                      selectedOptionColor="blue"
                      isLoading={emlPartiesLoading}
                      options={
                        emlParties?.map((provider) => {
                          return {
                            label: provider.Value!,
                            value: provider.Key!,
                          };
                        }) || []
                      }
                      className="relative-listbox"
                    />

                    <IntegerField
                      horizontal
                      label="Loyalty Points Transfer To Card Fee Percent"
                      name={`${fieldName}.value.FeeConfiguration.LoyaltyPointsTransferToCardFeePercent`}
                      precision={1}
                    />
                  </>
                </VStack>
              </Box>

              <Box
                w="100%"
                p={4}
                border="1px solid"
                borderColor="cherryButtonDark.100"
                borderRadius="md"
              >
                <HStack w="100%">
                  <Text mr="auto" fontSize="sm">
                    <b>Card Activation Configuration</b>
                  </Text>
                </HStack>

                <Divider mb={2} mt={1} />

                <VStack w="100%">
                  <>
                    <TextField
                      horizontal
                      label="Card Program Name"
                      name={`${fieldName}.value.CardActivationConfiguration.CardProgramName`}
                    />

                    <JsonTextAreaField
                      horizontal
                      label="Card Activation Theme Data"
                      name={`${fieldName}.value.CardActivationConfiguration.CardActivationThemeData`}
                      valueText={
                        program.value.CardActivationConfiguration
                          ?.CardActivationThemeData ?? ""
                      }
                      errors={errors}
                      setFieldValue={setFieldValue}
                      rows={10}
                    />
                  </>
                </VStack>
              </Box>
              <Box
                w="100%"
                p={4}
                border="1px solid"
                borderColor="cherryButtonDark.100"
                borderRadius="md"
              >
                <HStack w="100%">
                  <Text mr="auto" fontSize="sm">
                    <b>Card Transfer Limits Configuration</b>
                  </Text>
                </HStack>

                <Divider mb={2} mt={1} />

                <VStack w="100%">
                  <>
                    <SwitchField
                      horizontal
                      label="Is Limit Check Enabled"
                      name={`${fieldName}.value.CardProgramTransferLimit.IsLimitCheckedOnTransfer`}
                    />
                    <IntegerField
                      horizontal
                      label="Time Period Value"
                      name={`${fieldName}.value.CardProgramTransferLimit.TimePeriod.Quantity`}
                    />
                    <ChakraReactSelectField
                      horizontal
                      label="Time Period Type"
                      name={`${fieldName}.value.CardProgramTransferLimit.TimePeriod.Period`}
                      selectedOptionColor="blue"
                      showOnTop
                      options={[
                        {
                          label: "Day",
                          value: "Day",
                        },
                        {
                          label: "Week",
                          value: "Week",
                        },
                        {
                          label: "Month",
                          value: "Month",
                        },
                        {
                          label: "Year",
                          value: "Year",
                        },
                      ]}
                    />
                    <IntegerField
                      horizontal
                      label="Dollar Limit"
                      name={`${fieldName}.value.CardProgramTransferLimit.DollarLimit`}
                    />
                  </>
                </VStack>
              </Box>
            </VStack>
          )}
        </AccordionPanel>
      </>
    )}
  </AccordionItem>
);

interface EMLConfigurationDetailFormValues {
  AppDownloadUrlOverride?: string | null;
  DefaultDisbursementAccountId?: string | null;
  DefaultInstantGiftCardProgram?: number | null;
  DefaultInstantGiftInvitationSMSMessageBodyFormat?: string | null;
  DefaultPlasticGiftCardProgram?: number | null;
  DefaultReloadableCardProgram?: number | null;
  DefaultReloadableCardProgramInvitationSMSMessageBodyFormat?: string | null;
  DefaultSalaryPackagingProgram?: number | null;
  SelfServeMemberTerminals?:
    | CherryHubAdminApi.EmlMemberTerminalConfiguration[]
    | null;
  Programs?:
    | {
        key: string;
        value: CherryHubAdminApi.EmlCardProgram;
      }[]
    | null;
  MemberFilterConfiguration?: {
    RestrictCherryPayAppMembers?: boolean;
    UseBusinessDigitalMembershipActiveMemberFilter?: boolean;
    MemberFilterQuerystring?: string | null;
  };
}

interface EMLConfigurationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  emlConfiguration: CherryHubAdminApi.EMLConfiguration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (integration: CherryHubAdminApi.EMLConfiguration) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
  errors,
  emlConfiguration,
  businessId,
  setFieldValue,
}: EMLConfigurationDetailFormProps &
  FormikProps<EMLConfigurationDetailFormValues>) => {
  const memberFilterQueryModal = useDisclosure();

  const [memberFilterFieldName, setMemberFilterFieldName] =
    useState<string>("");
  const [existingMemberFilter, setExistingMemberFilter] = useState<string>();

  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    setFieldValue(memberFilterFieldName, query);
    memberFilterQueryModal.onClose();
  };

  const addMemberTerminal = (scrollTo?: boolean) => {
    if (!values.SelfServeMemberTerminals) {
      values.SelfServeMemberTerminals = [];
    }
    values.SelfServeMemberTerminals!.push({
      TerminalId: values.SelfServeMemberTerminals.length
        ? maxBy<CherryHubAdminApi.EmlMemberTerminalConfiguration>(
            values!.SelfServeMemberTerminals!,
            "TerminalId"
          )!.TerminalId + 1
        : 0,
      IsEnabled: true,
      MemberLookupMethod: MemberLookupMethod.MembershipNumber,
      CheckCode: uuid().replace(/-/g, ""),
    });
    setFieldValue("SelfServeMemberTerminals", values.SelfServeMemberTerminals);
    if (scrollTo)
      setTimeout(
        () =>
          document
            .querySelector(
              `.terminal-field-${values.SelfServeMemberTerminals?.length! - 1}`
            )
            ?.scrollIntoView({ behavior: "smooth" }),
        500
      );
  };

  const addProgram = (scrollTo?: boolean) => {
    if (!values.Programs) {
      values.Programs = [];
    }
    values.Programs!.push({
      key: "",
      value: {
        CompanyId: "",
        ProgramId: 0,
        ProgramType: EMLProgramType.Unknown,
        DefaultOfferExpiryDays: 0,
        DefaultPlasticExpiryMonths: 0,
        IsEnabled: true,
      },
    });
    setFieldValue("Programs", values.Programs);
    if (scrollTo)
      setTimeout(
        () =>
          document
            .querySelector(`.program-field-${values.Programs?.length! - 1}`)
            ?.scrollIntoView({ behavior: "smooth" }),
        500
      );
  };

  const getProgramList = (
    type: EMLProgramType,
    deliveryType?: EMLDeliveryType
  ) => {
    return values.Programs?.filter((program) => {
      return (
        program.value.ProgramType == type &&
        (deliveryType != null
          ? program.value.DeliveryTypes?.indexOf(deliveryType) != -1
          : true)
      );
    }).map((program) => {
      return { label: program.key, value: program.key };
    });
  };

  const { data: memberLookupMethods, isLoading: memberLookupMethodsLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.MemberLookupMethod
        ),
      []
    );

  const {
    data: allowableInputStringCharacters,
    isLoading: allowableInputStringCharactersLoading,
  } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.AllowableInputStringCharacters
      ),
    []
  );

  const { data: programTypes, isLoading: programTypesLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.ProgramType),
      []
    );

  const { data: deliveryTypes, isLoading: deliveryTypesLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.DeliveryType
        ),
      []
    );

  const { data: emlParties, isLoading: emlPartiesLoading } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(AdminApiEnumConfigurations.Party),
    []
  );

  return (
    <>
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {emlConfiguration && (
            <HStack w="100%">
              <Box flexGrow={1} pb={4}>
                <CopyText text={emlConfiguration?.Meta?.Id!} hover adjustMargin>
                  <Text fontSize="md" color="cherryUi.600">
                    {emlConfiguration?.id}
                  </Text>
                </CopyText>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}

          <VStack
            w="100%"
            maxW="1200px"
            alignItems="flex-start"
            spacing={4}
            pb={6}
          >
            <TextField
              horizontal
              label="Default Disbursement Account Id"
              name="DefaultDisbursementAccountId"
            />

            <ChakraReactSelectField
              horizontal
              label="Default Plastic Gift Card Program"
              name="DefaultPlasticGiftCardProgram"
              selectedOptionColor="blue"
              allowEmpty
              options={
                getProgramList(
                  EMLProgramType.PlasticGift,
                  EMLDeliveryType.Physical
                ) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              label="Default Instant Gift Card Program"
              name="DefaultInstantGiftCardProgram"
              selectedOptionColor="blue"
              allowEmpty
              options={getProgramList(EMLProgramType.InstantGift) || []}
              className="relative-listbox"
            />

            <ChakraReactSelectField
              horizontal
              label="Default Reloadable Card Program"
              name="DefaultReloadableCardProgram"
              selectedOptionColor="blue"
              allowEmpty
              options={getProgramList(EMLProgramType.Reloadable) || []}
              className="relative-listbox"
            />

            <ChakraReactSelectField
              horizontal
              label="Default Salary Packaging Program"
              name="DefaultSalaryPackagingProgram"
              selectedOptionColor="blue"
              allowEmpty
              options={getProgramList(EMLProgramType.SalaryPackaging) || []}
              className="relative-listbox"
            />
          </VStack>

          <Tabs variant="edit" w="100%" isLazy>
            <TabList>
              <HStack spacing={3} overflowX="auto">
                <Tab>Format</Tab>

                <Tab>Programs</Tab>

                <Tab>Self Serve Member Terminals</Tab>

                <Tab>Member Filtering</Tab>
              </HStack>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack w="100%">
                  <TextArea
                    horizontal
                    label="Default Reloadable Card Program Invitation SMS Message Body Format"
                    name="DefaultReloadableCardProgramInvitationSMSMessageBodyFormat"
                  />

                  <TextArea
                    horizontal
                    label="Default Instant Gift Invitation SMS Message Body Format"
                    name="DefaultInstantGiftInvitationSMSMessageBodyFormat"
                  />
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%">
                  {(values.Programs?.length ?? 0) == 0 && (
                    <VStack w="100%" p={4} spacing={2}>
                      <Text fontStyle="italic" fontSize="sm">
                        Programs has not been set.
                      </Text>
                      <Button
                        size="sm"
                        leftIcon={<PlusIcon />}
                        onClick={() => addProgram()}
                        colorScheme="cherryButton"
                      >
                        Program
                      </Button>
                    </VStack>
                  )}

                  {(values.Programs?.length ?? 0) > 0 && (
                    <>
                      <Button
                        leftIcon={<PlusIcon />}
                        onClick={() => addProgram(true)}
                        colorScheme="cherryButton"
                        alignSelf="flex-end"
                        size="sm"
                      >
                        Program
                      </Button>

                      <Accordion
                        w="100%"
                        allowMultiple
                        allowToggle
                        variant="custom-field"
                      >
                        <VStack w="100%">
                          {values.Programs?.length &&
                            values.Programs?.map((program, programIdx) =>
                              programView(
                                values.Programs!,
                                program,
                                programIdx,
                                `Programs[${programIdx}]`,
                                programTypes,
                                programTypesLoading,
                                deliveryTypes,
                                deliveryTypesLoading,
                                emlParties,
                                emlPartiesLoading,
                                getIn(
                                  errors,
                                  `Programs[${programIdx}].CardActivationConfiguration.CardActivationThemeData`
                                ),
                                setFieldValue
                              )
                            )}
                        </VStack>
                      </Accordion>
                    </>
                  )}
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%">
                  {(values.SelfServeMemberTerminals?.length ?? 0) == 0 && (
                    <VStack w="100%" p={4} spacing={2}>
                      <Text fontStyle="italic" fontSize="sm">
                        Self Serve Member Terminals has not been set.
                      </Text>
                      <Button
                        size="sm"
                        leftIcon={<PlusIcon />}
                        onClick={() => addMemberTerminal()}
                        colorScheme="cherryButton"
                      >
                        Self Serve Member Terminal
                      </Button>
                    </VStack>
                  )}

                  {(values.SelfServeMemberTerminals?.length ?? 0) > 0 && (
                    <>
                      <Button
                        leftIcon={<PlusIcon />}
                        onClick={() => addMemberTerminal(true)}
                        colorScheme="cherryButton"
                        alignSelf="flex-end"
                        size="sm"
                      >
                        Self Serve Member Terminal
                      </Button>

                      <Accordion
                        w="100%"
                        allowMultiple
                        allowToggle
                        variant="custom-field"
                      >
                        <VStack w="100%">
                          {values.SelfServeMemberTerminals?.length &&
                            values.SelfServeMemberTerminals?.map(
                              (memberTerminal, memberTerminalIdx) =>
                                memberTerminalView(
                                  values.SelfServeMemberTerminals!,
                                  memberTerminal,
                                  memberTerminalIdx,
                                  memberLookupMethods,
                                  memberLookupMethodsLoading,
                                  allowableInputStringCharacters,
                                  allowableInputStringCharactersLoading,
                                  `SelfServeMemberTerminals[${memberTerminalIdx}]`,
                                  getIn(
                                    errors,
                                    `SelfServeMemberTerminals[${memberTerminalIdx}].ThemeData`
                                  ),
                                  setFieldValue,
                                  memberFilterQueryModal.onOpen,
                                  setMemberFilterFieldName,
                                  setExistingMemberFilter
                                )
                            )}
                        </VStack>
                      </Accordion>
                    </>
                  )}
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack w="100%" alignItems="flex-start">
                  <Box
                    w="100%"
                    p={4}
                    border="1px solid"
                    borderColor="cherryButtonDark.100"
                    borderRadius="md"
                  >
                    <HStack w="100%">
                      <Text mr="auto" fontSize="sm">
                        <b>CherryPay App Member Filtering</b>
                      </Text>
                    </HStack>

                    <Divider mb={4} mt={2} />

                    <VStack w="100%" spacing={4}>
                      <SwitchField
                        horizontal
                        label="Restrict CherryPay App Members"
                        name="MemberFilterConfiguration.RestrictCherryPayAppMembers"
                      />

                      <SwitchField
                        horizontal
                        label="Use Business Digital Membership Active Member Filter"
                        name="MemberFilterConfiguration.UseBusinessDigitalMembershipActiveMemberFilter"
                        isDisabled={
                          !values.MemberFilterConfiguration
                            ?.RestrictCherryPayAppMembers
                        }
                      />

                      <TextArea
                        horizontal
                        label="Member Filter Querystring"
                        name="MemberFilterConfiguration.MemberFilterQuerystring"
                        isReadOnly
                        isDisabled={
                          !values.MemberFilterConfiguration
                            ?.RestrictCherryPayAppMembers ||
                          values.MemberFilterConfiguration
                            ?.UseBusinessDigitalMembershipActiveMemberFilter
                        }
                        rightElement={
                          <Button
                            onClick={() => {
                              setMemberFilterFieldName(
                                "MemberFilterConfiguration.MemberFilterQuerystring"
                              );
                              setExistingMemberFilter(
                                values.MemberFilterConfiguration
                                  ?.MemberFilterQuerystring ?? ""
                              );
                              memberFilterQueryModal.onOpen();
                            }}
                            alignSelf="flex-end"
                            colorScheme="cherryButton"
                            isDisabled={
                              !values.MemberFilterConfiguration
                                ?.RestrictCherryPayAppMembers ||
                              values.MemberFilterConfiguration
                                ?.UseBusinessDigitalMembershipActiveMemberFilter
                            }
                          >
                            Filters
                          </Button>
                        }
                      />
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {!isValid && (
            <ErrorMessage>One or more validation has failed.</ErrorMessage>
          )}

          <HStack width="100%" justifyContent="center" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>

      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        onSuccess={onSuccessMemberFilterQuery}
        businessId={businessId}
        memberQueryFilter={existingMemberFilter ?? ""}
      />
    </>
  );
};

export const EMLConfigurationDetailForm = withFormik<
  EMLConfigurationDetailFormProps,
  EMLConfigurationDetailFormValues
>({
  mapPropsToValues: ({ emlConfiguration }) => {
    const result = cloneDeep(emlConfiguration);
    return {
      DefaultDisbursementAccountId: result?.DefaultDisbursementAccountId,
      DefaultInstantGiftCardProgram: result?.DefaultInstantGiftCardProgram,
      DefaultInstantGiftInvitationSMSMessageBodyFormat:
        result?.DefaultInstantGiftInvitationSMSMessageBodyFormat,
      DefaultPlasticGiftCardProgram: result?.DefaultPlasticGiftCardProgram,
      DefaultReloadableCardProgram: result?.DefaultReloadableCardProgram,
      DefaultReloadableCardProgramInvitationSMSMessageBodyFormat:
        result?.DefaultReloadableCardProgramInvitationSMSMessageBodyFormat,
      DefaultSalaryPackagingProgram: result?.DefaultSalaryPackagingProgram,
      SelfServeMemberTerminals: result?.SelfServeMemberTerminals?.map(
        (terminal) => {
          const themeData = terminal.ThemeData;
          if (themeData) {
            terminal.ThemeData =
              typeof themeData == "string"
                ? themeData
                : JSON.stringify(themeData, null, 2);
          }
          return terminal;
        }
      ),
      Programs:
        result?.Programs &&
        Object.entries(result?.Programs).map(([key, value]) => {
          const themeData =
            value.CardActivationConfiguration?.CardActivationThemeData;
          if (themeData) {
            value.CardActivationConfiguration!.CardActivationThemeData =
              typeof themeData == "string"
                ? themeData
                : JSON.stringify(themeData, null, 2);
          }
          return {
            key,
            value,
          };
        }),
      MemberFilterConfiguration: result?.MemberFilterConfiguration ?? {
        RestrictCherryPayAppMembers: false,
        UseBusinessDigitalMembershipActiveMemberFilter: false,
        MemberFilterQuerystring: null,
      },
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    let isValid = true;
    values.Programs?.forEach((program, programIdx) => {
      if (
        values.Programs?.filter(
          (compare, compareIdx) =>
            compareIdx != programIdx && compare.key == program.key
        )?.length ??
        0 > 1
      ) {
        setFieldError(
          `Programs[${programIdx}].key`,
          "Program Id should be unique across the EML Programs"
        );
        isValid = false;
      }
    });

    if (!isValid) return false;

    var cardProgram = {};
    values.Programs?.forEach((program, idx) => {
      const result = { ...program.value } as CherryHubAdminApi.EmlCardProgram;
      if (result.CardActivationConfiguration) {
        result.CardActivationConfiguration.CardActivationThemeData = result
          .CardActivationConfiguration.CardActivationThemeData
          ? JSON.parse(
              result.CardActivationConfiguration
                .CardActivationThemeData as string
            )
          : undefined;
      }

      result.MinimumInitialCardValue = !result.MinimumInitialCardValue
        ? undefined
        : result.MinimumInitialCardValue;

      result.MinimumAddFundsToCardValue = !result.MinimumAddFundsToCardValue
        ? undefined
        : result.MinimumAddFundsToCardValue;

      result.MinimumPointsTransferToCardValue =
        !result.MinimumPointsTransferToCardValue
          ? undefined
          : result.MinimumPointsTransferToCardValue;

      result.CardOrderOTPValidationAttemptLimit =
        !result.CardOrderOTPValidationAttemptLimit
          ? undefined
          : result.CardOrderOTPValidationAttemptLimit;

      if (result.FeeConfiguration) {
        result.FeeConfiguration.FundsLoadFeePercent = !result.FeeConfiguration
          .FundsLoadFeePercent
          ? undefined
          : result.FeeConfiguration.FundsLoadFeePercent;

        result.FeeConfiguration.LoyaltyPointsTransferToCardFeePercent = !result
          .FeeConfiguration.LoyaltyPointsTransferToCardFeePercent
          ? undefined
          : result.FeeConfiguration.LoyaltyPointsTransferToCardFeePercent;
      }
      (
        cardProgram as {
          [key: string]: CherryHubAdminApi.EmlCardProgram;
        }
      )[program.key] = result;
    });

    values.SelfServeMemberTerminals?.forEach((terminal, idx) => {
      terminal.ThemeData = terminal.ThemeData
        ? JSON.parse(terminal.ThemeData as string)
        : undefined;
    });

    const emlConfigurationDoc: Partial<CherryHubAdminApi.EMLConfigurationUpdate> =
      {
        AppDownloadUrlOverride: values.AppDownloadUrlOverride,
        DefaultDisbursementAccountId: values.DefaultDisbursementAccountId,
        DefaultInstantGiftCardProgram: values.DefaultInstantGiftCardProgram,
        DefaultInstantGiftInvitationSMSMessageBodyFormat:
          values.DefaultInstantGiftInvitationSMSMessageBodyFormat,
        DefaultPlasticGiftCardProgram: values.DefaultPlasticGiftCardProgram,
        DefaultReloadableCardProgram: values.DefaultReloadableCardProgram,
        DefaultReloadableCardProgramInvitationSMSMessageBodyFormat:
          values.DefaultReloadableCardProgramInvitationSMSMessageBodyFormat,
        DefaultSalaryPackagingProgram: values.DefaultSalaryPackagingProgram,
        SelfServeMemberTerminals: values.SelfServeMemberTerminals,
        Programs: cardProgram,
        MemberFilterConfiguration: values.MemberFilterConfiguration,
      };

    let result: ApiResult<CherryHubAdminApi.EMLConfiguration> | null = null;

    if (props.emlConfiguration == null) {
      result = await props.apiClient.createEMLConfiguration(
        props.businessId,
        emlConfigurationDoc
      );
    } else {
      result = await props.apiClient.updateEMLConfiguration(
        props.businessId,
        emlConfigurationDoc
      );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
  validationSchema,
})(InnerForm);
